/////////////////
// HEURISTIC POLICY GLOBAL CONSTANTS
//
// Note: local constants are contained
// within their respective directories.
/////////////////

export const embeddedInterventionTypes = {
  silentSound: 'silentSound',
};

export const interventionTypes = {
  clap: 'clap',
  lipsync: 'lipsync',
  sayit: 'sayit',
  spellout: 'spellout',
  spelloutBefore: 'spelloutBefore',
  read: 'read',
  none: 'none',
  toofast: 'toofast',
  repeatsentence: 'repeatsentence',
  repeatsentenceErrorsEmphasized: 'repeatsentenceErrorsEmphasized',
  simpledefinition: 'simpledefinition',
  skip: 'skip',
  readwithme: 'readwithme',
  initiateReadWithMe: 'initiateReadWithMe',
  funfact: 'funfact',
  tense: 'tense',
  sight: 'sight',
  giveword: 'giveword',
  phonemeGiveWord: 'phonemeGiveWord',
  phonemeLipsync: 'phonemeLipsync',
  videoLipsync: 'videoLipsync',
  comprehension: 'comprehension',
  cognate: 'cognate',
  prediction: 'prediction',
  vocabularyWithPictures: 'vocabularyWithPictures',
  morpheme: 'morpheme',
  riddle: 'riddle',
  letterFlies: 'letterFlies',
  sayASound: 'sayASound',
  soundSeeker: 'soundSeeker',
  name1: 'name1',
  name2: 'name2',
  soundout1: 'soundout1',
  soundout2: 'soundout2',
  soundout3: 'soundout3',
  elkoninBox0: 'elkoninBox0',
  elkoninBox1: 'elkoninBox1',
  elkoninBox2: 'elkoninBox2',
  elkoninBox3: 'elkoninBox3',
  elkoninBox4: 'elkoninBox4',
  upAndDown1: 'upAndDown1',
  upAndDown2: 'upAndDown2',
  upAndDown3: 'upAndDown3',
  wordDisplay: 'wordDisplay',
  changeOnePart1: 'changeOnePart1',
  giveAndTake1: 'giveAndTake1',
  rhyming1: 'rhyming1',
  rhymingQuiz1: 'rhymingQuiz1',
  sayOneSound1: 'sayOneSound1',
  wordInvestigator1: 'wordInvestigator1',
  wordInvestigator2: 'wordInvestigator2',
  wordScramble1: 'wordScramble1',
  GSF: 'GSF',
  msylSegment: 'msylSegment',
  ...embeddedInterventionTypes,
};

/**
 * Iterates over the values of the `embeddedInterventionTypes` object
 * and constructs an object where each intervention type is mapped to a string
 * with the format "word_<interventionType>".
 *
 * @returns {Object} A mapping of intervention types to their corresponding state field names.
 */
const embeddedInterventionRequiredFields = Object.values(
  embeddedInterventionTypes
).reduce((acc, cur) => {
  // For each intervention type, add a key-value pair where the key is the intervention type and the value is "word_<type>"
  acc[cur] = [`word_${cur}`];
  return acc; // Return the accumulator object with the newly added key-value pair
}, {}); // Initialize the accumulator as an empty object

export const interventionClasses = {
  phonemeLipsync: ['phonemeLipsync', 'videoLipsync', 'lipsync'],
  lipsync: ['phonemeLipsync', 'videoLipsync', 'lipsync'],
  videoLipsync: ['phonemeLipsync', 'videoLipsync', 'lipsync'],
  spellout: ['spellout', 'spelloutBefore', 'soundout', 'sayit'],
  spelloutBefore: ['spellout', 'spelloutBefore', 'soundout', 'sayit'],
  soundout: ['spellout', 'spelloutBefore', 'soundout', 'sayit'],
  sayit: ['spellout', 'spelloutBefore', 'soundout', 'sayit'],
};

// Don't consider returning these intervention types
// This const is used to disable intervention types that the studentapp doesn't yet support
export const disabledInterventionTypes = {
  decoding: 'decoding',
  videoLipsync: 'videoLipsync',
};

/////////////////
// INTERVENTION CONSTANTS
/////////////////

export const interventionRequiredFields = {
  simpledefinition: ['word_DEFINITION'],
  clap: ['word_DEFINITION'],
  funfact: ['word_FUN_FACT'], // todo add this field to state in lexa-studentapp
  lipsync: ['word_VIDEO_URL'],
  sight: ['word_SIGHT'],
  // decoding: ['decodingPR'],
  sayit: ['word_GRAPHEMIC_BREAKDOWN'],
  phonemeGiveWord: [
    'word_PHON',
    'word_GRAPHEMIC_BREAKDOWN',
    'word_PHON_GRAPHEME_MATCH',
  ],
  phonemeLipsync: ['word_PHONEME_VIDEO_URL'],
  spellout: ['word_GRAPHEMIC_BREAKDOWN'],
  spelloutBefore: ['word_GRAPHEMIC_BREAKDOWN'],
  comprehension: ['phrase_questions'],
  cognate: ['word_COGNATE'],
  prediction: ['phrase_predictionQuestions'],
  vocabularyWithPictures: ['word_DEFINITION', 'word_IMG_URL'],
  morpheme: [
    'word_MORPHEME',
    'word_MORPHEME_POSITION',
    'word_MORPHEME_MEANING',
    'word_MORPHEME_ALTS',
  ],
  riddle: ['word_RIDDLE', 'word_RIDDLE_ANSWER'],
  name1: [
    'word_PHON',
    'word_NAME_FLAG',
    'word_NAME_NATIONALITY',
    'word_NAME_MEANING',
    'word_GRAPHEMIC_BREAKDOWN',
  ],
  name2: ['word_NAME_PERSON', 'word_FUNFACTS_DESCRIPTION'],
  letterFlies: [
    'word_PHON',
    'word_GRAPHEMIC_BREAKDOWN',
    'word_PHON_GRAPHEME_MATCH',
  ],
  sayASound: [
    'word_PHON',
    'word_GRAPHEMIC_BREAKDOWN',
    'word_PHON_GRAPHEME_MATCH',
    'word_PHONEME_VIDEO_URL',
  ],
  soundSeeker: ['word_PHON'],
  soundout1: [
    'word_PHON',
    'word_GRAPHEMIC_BREAKDOWN',
    'word_PHON_GRAPHEME_MATCH',
  ],
  soundout2: [
    'word_PHON',
    'word_GRAPHEMIC_BREAKDOWN',
    'word_PHON_GRAPHEME_MATCH',
  ],
  soundout3: [
    'word_PHON',
    'word_GRAPHEMIC_BREAKDOWN',
    'word_PHON_GRAPHEME_MATCH',
  ],
  elkoninBox0: [
    'word_PHON',
    'word_GRAPHEMIC_BREAKDOWN',
    'word_PHON_GRAPHEME_MATCH',
  ],
  elkoninBox1: [
    'word_PHON',
    'word_GRAPHEMIC_BREAKDOWN',
    'word_PHON_GRAPHEME_MATCH',
  ],
  elkoninBox2: [
    'word_PHON',
    'word_GRAPHEMIC_BREAKDOWN',
    'word_PHON_GRAPHEME_MATCH',
  ],
  elkoninBox3: [
    'word_PHON',
    'word_GRAPHEMIC_BREAKDOWN',
    'word_PHON_GRAPHEME_MATCH',
  ],
  elkoninBox4: [
    'word_PHON',
    'word_GRAPHEMIC_BREAKDOWN',
    'word_PHON_GRAPHEME_MATCH',
  ],
  upAndDown1: [
    'word_PHON',
    'word_GRAPHEMIC_BREAKDOWN',
    'word_PHON_GRAPHEME_MATCH',
  ],
  upAndDown2: [
    'word_PHON',
    'word_GRAPHEMIC_BREAKDOWN',
    'word_PHON_GRAPHEME_MATCH',
  ],
  upAndDown3: [
    'word_PHON',
    'word_GRAPHEMIC_BREAKDOWN',
    'word_PHON_GRAPHEME_MATCH',
  ],
  wordDisplay: [
    'word_PHON',
    'word_GRAPHEMIC_BREAKDOWN',
    'word_DEFINITION',
    'word_IMG_URL',
  ],
  changeOnePart1: [
    'word_PHON',
    'word_GRAPHEMIC_BREAKDOWN',
    'word_PHON_GRAPHEME_MATCH',
  ],
  giveAndTake1: [
    'word_PHON',
    'word_GRAPHEMIC_BREAKDOWN',
    'word_PHON_GRAPHEME_MATCH',
  ],
  rhyming1: [
    'word_PHON',
    'word_GRAPHEMIC_BREAKDOWN',
    'word_PHON_GRAPHEME_MATCH',
    'word_RHYME',
  ],
  rhymingQuiz1: ['word_RHYME', 'word_NSYL'],
  sayOneSound1: ['word_PHON', 'word_GRAPHEMIC_BREAKDOWN', 'word_IMG_URL'],
  wordInvestigator1: ['word_PHON', 'word_IMG_URL'],
  wordInvestigator2: ['word_IMG_URL'],
  wordScramble1: [
    'word_PHON',
    'word_GRAPHEMIC_BREAKDOWN',
    'word_PHON_GRAPHEME_MATCH',
  ],
  tense: ['tenseError'],
  msylSegment: [
    'word_SYLLABLE_ENCODING',
    'word_MSYL_SPLIT_CODE',
    'word_MSYL_HAS_SAFE_SYLLABLE_COUNT',
  ],
  ...embeddedInterventionRequiredFields,
};

export const interventionDefaultReadyScores = {
  funfact: -0.001,
};

/////////////////
// RULE CONSTANTS
/////////////////

export const ruleTypes = {
  DEFAULT: 'default', // corresponds to an instantiation of the Rule base class
  DIFFICULTY: 'wordDifficultyCheck',
  WORD_AREA_CHECK: 'wordAreaCheck',
  READING_DIFFICULTY: 'wordReadingDifficultyCheck',
  LETTER: 'letterCheck',
  SYLLABLE: 'syllableCheck',
  ESL: 'isESL',
  EARLY_READER: 'isEarlyReader',
  DECODING: 'decodingCheck',
  STUDENT_AREA: 'studentAreaCheck',
  PHONEME: 'phonemeCheck',
  GRAPHEME: 'graphemeCheck',
  AREA_PHONEME: 'areaPhonemeCheck',
  RUSHING: 'isRushingCheck',
  SKIP: 'isSkipError',
  INTERVENTION_OCCURRENCE: 'interventionOccurrenceCheck',
  RECENT_OCCURRENCE: 'recentlyOccurredCheck',
  RELATIVE_RECENT_OCCURRENCE: 'relativeRecentlyOccurredCheck',
  SIGHT: 'isSightWord',
  CLOSE: 'isCloseError',
  PHRASE: 'phraseIndexCheck',
  OR: 'or',
  AND: 'and',
  END_OF_PHRASE: 'isEndOfPhrase',
  FRAME_TIME: 'frametimeCheck',
  STEM: 'isStemError',
  MOST_RECENT_ACCURACY: 'mostRecentAccuracyCheck',
  LETTERS_VS_PHONEMES: 'lettersVsPhonemesCheck',
  NGRAM_COUNT: 'ngramCountCheck',
};

export const ruleInputTypes = {
  studentAREA: 'studentAREA',
  word_AREA_AOA: 'word_AREA_AOA',
  word_AREA_discrim: 'word_AREA_discrim',
  word_NSYL: 'word_NSYL',
  word_NLET: 'word_NLET',
  word_NPHON: 'word_NPHON',
  word_SIGHT: 'word_SIGHT',
  word_GRAPHEMIC_BREAKDOWN: 'word_GRAPHEMIC_BREAKDOWN',
  word_log_ngram_counts: 'word_log_ngram_counts',
  decodingPR: 'decodingPR',
  rushingDetected: 'rushingDetected',
  phraseIndex: 'phraseIndex',
  endOfPhrase: 'endOfPhrase',
  frameTimes: 'frameTimes',
  wordIndex: 'wordIndex',
  mostRecentAccuracy: 'mostRecentAccuracy',
};

// This list delineates input types that are
// currently only available/applicable at phrase level
export const phraseLevelRuleInputTypes = ['rushingDetected', 'frameTimes'];

export const masteryConditionEnums = {
  ALMOST_CERTAINLY_MASTERED: 'ALMOST_CERTAINLY_MASTERED',
  LIKELY_MASTERED: 'LIKELY_MASTERED',
  MAYBE_MASTERED: 'MAYBE_MASTERED',
  PROBABLY_MASTERED: 'PROBABLY_MASTERED',
  APPROPRIATELY_CHALLENGING: 'APPROPRIATELY_CHALLENGING',
  ZPD_WIDE: 'ZPD_WIDE',
  VERY_CHALLENGING: 'VERY_CHALLENGING',
  SUPER_CHALLENGING: 'SUPER_CHALLENGING',
};

export const masteryConditionValues = {
  ALMOST_CERTAINLY_MASTERED: 1,
  LIKELY_MASTERED: 0.5,
  PROBABLY_MASTERED: 0,
  MAYBE_MASTERED: -0.25,
  APPROPRIATELY_CHALLENGING: [-0.25, -0.5],
  ZPD_WIDE: [-1.5, -2.5],
  VERY_CHALLENGING: -1,
  SUPER_CHALLENGING: -2,
};

export const frameTimeEnums = {
  LONG: 'LONG',
  SHORT: 'SHORT',
};

export const frameTimeConditionValues = {
  LONG: 2,
  SHORT: 0.6,
};

/////////////////
// INTERVENE SELECTOR CONSTANTS
/////////////////
export const interventionSituationTypes = {
  WORD: 'WORD',
  PHRASE: 'PHRASE',
  END_OF_STORY: 'END_OF_STORY',
};

export const wcpmPivot = 80;
